import { Box, Card, DottedList, DottedListItem, Flex, Grid, Inline, Paragraph, Stack, Status } from "@eworkit/iris";
import { RouteCard } from "components-new";
import React, { FC, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import { LazyUserAvatar } from "@/components/avatars";
import { ConsultantProfileContext, UserContext } from "@/components/User";
import {
  getProfileRole,
  getProfileStatusTranslationKey,
  getProfileStatusVariant,
  IShortConsultantProfile,
} from "@/data/consultant-profiles";
import { formatFullName } from "@/data/persons";
import { useToggle } from "@/hooks/Toggle";
import { RequestToJoinDialogModal } from "@/views/components/Company/RequestToJoin";
import { CannotAcceptRequestToJoinDialogModal } from "@/views/components/Company/RequestToJoin/CannotAcceptRequestToJoinModal";
import { router } from "@/views/router";

interface TeamRecordContentProps {
  record: IShortConsultantProfile;
}

const TeamRecordContent: FC<TeamRecordContentProps> = ({ record }) => {
  const { t } = useTranslation("session");
  const { currentProfileId } = useContext(ConsultantProfileContext);

  const statusVariant = getProfileStatusVariant(record);
  const statusText = t(getProfileStatusTranslationKey(record));

  return (
    <Grid columns={["auto 1fr", null, "auto 1fr auto"]} sx={{ width: "100%" }}>
      <Box gridColumnStart={1} gridColumnEnd={2} gridRowStart={1} gridRowEnd={1}>
        <LazyUserAvatar user={record} />
      </Box>
      <Box gridColumnStart={[1, null, 2]} gridColumnEnd={3} gridRowStart={[2, null, 1]} gridRowEnd={[2, null, 1]}>
        <Stack>
          <Inline gap="xxs">
            <Paragraph size="p-md" bold>
              {formatFullName(record)}
            </Paragraph>
            {currentProfileId === record.id && (
              <Paragraph size="p-md" color="n700">
                ({t("COMPANY.TEAM.YOU")})
              </Paragraph>
            )}
          </Inline>

          <DottedList responsive>
            <DottedListItem>
              {t(
                `CONSULTANT_PROFILES.DETAIL.PROFILE_ROLE.${getProfileRole(
                  record.companyInvitation && record.status === "PENDING"
                    ? record.companyInvitation.type
                    : record.companyRole,
                  record.requestToJoin,
                )}`,
              )}
            </DottedListItem>
            <DottedListItem>{record.email}</DottedListItem>
          </DottedList>
        </Stack>
      </Box>
      <Flex
        gridColumnStart={[2, null, 3]}
        gridColumnEnd={[3, null, 4]}
        gridRowStart={1}
        gridRowEnd={1}
        justifyContent="flex-end"
        alignItems="center">
        {statusVariant && statusText && <Status variant={statusVariant}>{statusText}</Status>}
      </Flex>
    </Grid>
  );
};

interface ITeamRecordProps {
  record: IShortConsultantProfile;
  onUpdate?: () => void;
}

export const TeamRecord: FC<ITeamRecordProps> = ({ record, onUpdate }) => {
  const User = useContext(UserContext);
  const isAdmin = User.hasCompanyAdminPrivileges();

  const [isModalOpen, toggleIsModalOpen] = useToggle(false);

  const profileDetailsPath = router.get("consultant-profiles.detail", { consultantProfileId: record.id }).path;

  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      toggleIsModalOpen();
    },
    [toggleIsModalOpen],
  );

  if (record.requestToJoin && record.requestToJoin.status === "PENDING") {
    return (
      <>
        <Card onClick={handleCardClick}>
          <TeamRecordContent record={record} />
        </Card>
        <RequestToJoinDialogModal
          onClose={toggleIsModalOpen}
          onSuccess={onUpdate}
          open={isModalOpen && isAdmin}
          requestToJoin={record.requestToJoin}
          existingRole={record.companyRole}
          user={record}
        />
        <CannotAcceptRequestToJoinDialogModal onClose={toggleIsModalOpen} open={isModalOpen && !isAdmin} size="xs" />
      </>
    );
  }

  return (
    <RouteCard path={profileDetailsPath}>
      <TeamRecordContent record={record} />
    </RouteCard>
  );
};
